// @ts-check
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminSiteSettingsService from '../services/adminSiteSettingsService';
import FileUpload from './FileUpload';

// Required image count per pattern — mirrors PATTERN_IMAGE_COUNT in
// frontend-next/src/components/marketing/CraftedConsciouslyImages.jsx; keep in sync.
const PATTERNS = [
  {
    value: 'single_landscape',
    label: 'Single Landscape',
    desc: '1 wide image',
    count: 1,
    preview: <div className="bg-foreground/70 rounded-sm w-full h-full" />,
  },
  {
    value: 'two_portrait',
    label: 'Two Portrait',
    desc: '2 tall images side by side',
    count: 2,
    preview: (
      <div className="flex gap-0.5 h-full">
        <div className="bg-foreground/70 rounded-sm flex-1" />
        <div className="bg-foreground/70 rounded-sm flex-1" />
      </div>
    ),
  },
  {
    value: 'two_landscape',
    label: 'Two Landscape',
    desc: '2 wide images side by side',
    count: 2,
    preview: (
      <div className="flex gap-0.5 h-full">
        <div className="bg-muted-foreground rounded-sm flex-1" />
        <div className="bg-muted-foreground rounded-sm flex-1" />
      </div>
    ),
  },
  {
    value: 'three_portrait',
    label: 'Three Portrait',
    desc: '3 tall images side by side',
    count: 3,
    preview: (
      <div className="flex gap-0.5 h-full">
        <div className="bg-foreground/70 rounded-sm flex-1" />
        <div className="bg-foreground/70 rounded-sm flex-1" />
        <div className="bg-foreground/70 rounded-sm flex-1" />
      </div>
    ),
  },
  {
    value: 'landscape_two_portrait',
    label: 'Landscape + Two Portrait',
    desc: '1 wide left, 2 tall right',
    count: 3,
    preview: (
      <div className="flex gap-0.5 h-full">
        <div className="bg-muted-foreground rounded-sm" style={{ width: '60%' }} />
        <div className="flex flex-col gap-0.5" style={{ width: '40%' }}>
          <div className="bg-foreground/70 rounded-sm flex-1" />
          <div className="bg-foreground/70 rounded-sm flex-1" />
        </div>
      </div>
    ),
  },
];

const HEIGHTS = [
  { value: 'compact', label: 'Compact' },
  { value: 'standard', label: 'Standard' },
  { value: 'tall', label: 'Tall' },
];

const DEFAULT_PATTERN = 'single_landscape';
const DEFAULT_HEIGHT = 'standard';

const patternCount = (pattern) => PATTERNS.find((p) => p.value === pattern)?.count || 1;

const CraftedConsciouslyImagesManager = () => {
  const toast = useToast();
  const [pattern, setPattern] = useState(DEFAULT_PATTERN);
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const [images, setImages] = useState(/** @type {string[]} */ (['']));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const setting = await adminSiteSettingsService.getSetting('crafted_consciously_images_config');
        const parsed = JSON.parse(setting.value);
        const loadedPattern = parsed.pattern || DEFAULT_PATTERN;
        const count = patternCount(loadedPattern);
        const loadedImages = Array.isArray(parsed.images) ? parsed.images : [];
        setPattern(loadedPattern);
        setHeight(parsed.height || DEFAULT_HEIGHT);
        setImages(Array.from({ length: count }, (_, i) => loadedImages[i] || ''));
      } catch {
        // Not configured yet — start blank, same as TrustPillarsManager's first-use fallback.
        setPattern(DEFAULT_PATTERN);
        setHeight(DEFAULT_HEIGHT);
        setImages(Array.from({ length: patternCount(DEFAULT_PATTERN) }, () => ''));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const selectPattern = (value) => {
    const count = patternCount(value);
    setPattern(value);
    setImages((prev) => Array.from({ length: count }, (_, i) => prev[i] || ''));
  };

  const setImageAt = (index, url) => {
    setImages((prev) => prev.map((existing, i) => (i === index ? url : existing)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminSiteSettingsService.bulkUpdate({
        crafted_consciously_images_config: JSON.stringify({ pattern, height, images }),
      });
      toast.success('Crafted Consciously images saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save images');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading images...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium mb-3">Layout Pattern</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {PATTERNS.map((p) => {
            const selected = pattern === p.value;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => selectPattern(p.value)}
                className={`flex flex-col border-2 rounded-lg p-2 transition-all text-left ${
                  selected
                    ? 'border-foreground bg-muted/50 ring-1 ring-foreground'
                    : 'border-border hover:border-muted-foreground'
                }`}
              >
                <div className="w-full aspect-[4/3] mb-2">{p.preview}</div>
                <span className={`text-xs font-medium ${selected ? 'text-foreground' : 'text-foreground/80'}`}>
                  {p.label}
                </span>
                <span className="text-[10px] text-muted-foreground/70 leading-tight">{p.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium mb-3">Section Height</h4>
        <div className="flex gap-3">
          {HEIGHTS.map((h) => {
            const selected = height === h.value;
            return (
              <button
                key={h.value}
                type="button"
                onClick={() => setHeight(h.value)}
                className={`px-4 py-2 rounded-lg text-xs font-medium border-2 transition-all ${
                  selected
                    ? 'border-foreground bg-muted/50 text-foreground'
                    : 'border-border text-muted-foreground hover:border-muted-foreground'
                }`}
              >
                {h.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium mb-3">
          Images ({images.filter(Boolean).length}/{images.length})
        </h4>
        <p className="text-xs text-muted-foreground/70 mb-3">
          The section only appears on the homepage once every slot below has an image.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((url, index) => (
            <div key={index}>
              <label className="block text-xs font-medium mb-1">Image {index + 1}</label>
              {url ? (
                <div className="relative border border-border rounded-lg overflow-hidden">
                  <img src={url} alt="" className="w-full h-32 object-cover" />
                  <button
                    type="button"
                    onClick={() => setImageAt(index, '')}
                    className="absolute top-1.5 right-1.5 p-1 bg-black/60 hover:bg-black/80 text-white rounded-full"
                    title="Remove image"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <FileUpload
                  uploadType="about-media"
                  accept="image/*"
                  multiple={false}
                  maxFiles={1}
                  onUploadComplete={(files) => {
                    const uploaded = (files || []).find((f) => f.uploaded && f.url);
                    if (uploaded) setImageAt(index, uploaded.url);
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/50 rounded-lg text-sm"
        >
          {saving ? 'Saving...' : 'Save Images'}
        </button>
      </div>
    </div>
  );
};

export default CraftedConsciouslyImagesManager;
